import React, { useCallback, useEffect, useRef } from 'react'
import Button from '@mui/material/Button'
import Tooltip from '@mui/material/Tooltip'
import CloudDownloadIcon from '@mui/icons-material/Download'
import CircularProgress from '@mui/material/CircularProgress'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'

import { useAppSelector } from 'redux/Hook'
import { exportContributionsData } from 'redux/thunks/TextAnalysis'
import { useAlert } from 'components/Shared/Alert'
import { downloadFile } from 'lib/utility/DownloadFile'
import { EXPORT_LIMIT } from '../Constants'

interface ExportProps {
  hide?: boolean
}

const Export = ({ hide }: ExportProps) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { showAlert } = useAlert()
  const { data, total, contributions } = useAppSelector(
    (state) => state.textAnalysis.main,
  )
  const {
    pending,
    done,
    error,
    data: exportData,
  } = contributions.exportContributions

  const isMounted = useRef(true)

  const isExporting = pending && !done
  const noData = !data || !data.length

  const handleExportClick = () => {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
    isMounted.current = false
    dispatch(exportContributionsData({ timezone }))
  }

  const downloadAsCSV = useCallback(() => {
    if (!exportData) return

    const { data, headers }: any = exportData
    const translatedData = `\ufeff${data}`
    const filename = headers?.['content-disposition']?.split('filename=')[1]
    const mimeType = headers?.['content-type']?.split(';')[0]

    downloadFile(translatedData, filename, mimeType)

    showAlert({
      type: 'success',
      title: t('textAnalysis.export.downloadSuccess'),
    })
  }, [exportData, showAlert])

  useEffect(() => {
    if (!pending && !isMounted.current) {
      isMounted.current = true
      if (done) {
        downloadAsCSV()
      } else if (error) {
        showAlert({
          type: 'error',
          title: t('textAnalysis.export.downloadFailed'),
        })
      }
    }
  }, [pending, done, error, showAlert])

  if (hide) return null

  const startIcon = isExporting ? (
    <CircularProgress color="inherit" size={20} />
  ) : (
    <CloudDownloadIcon />
  )

  const buttonText = isExporting
    ? t('textAnalysis.export.loading')
    : t('textAnalysis.export.name')

  const isDisabled = noData || total > EXPORT_LIMIT

  const tooltipText = isDisabled
    ? t('textAnalysis.export.tooltipDownloadLimit', {
        limit: EXPORT_LIMIT,
      })
    : ''

  const ariaLabelText = isDisabled
    ? t('textAnalysis.export.tooltipDownloadDisabled') + tooltipText
    : t('textAnalysis.export.tooltipDownloadEnabled')

  return (
    <Tooltip title={tooltipText} placement="top">
      <span
        role="button"
        aria-disabled={isDisabled ? 'true' : 'false'}
        tabIndex={isDisabled ? 0 : -1}
        aria-label={ariaLabelText}
        data-testid="ta-export-button"
      >
        <Button
          variant="outlined"
          startIcon={startIcon}
          disabled={isDisabled}
          onClick={handleExportClick}
          sx={{ mr: '1rem', height: '40px' }}
        >
          {buttonText}
        </Button>
      </span>
    </Tooltip>
  )
}

export default Export
